'use client'

import { useMemo, useState } from 'react'
import { calcTiling } from '@/lib/calculators/formulas'
import { ToolField, ToolResultCard, ToolResultRow, ToolSelect, toolCurrency, toolNum } from './tool-inputs'

type Pattern = 'straight' | 'diagonal' | 'herringbone' | 'complex'

export function TilingCalculatorTool() {
  const [area, setArea] = useState('20')
  const [tileL, setTileL] = useState('600')
  const [tileW, setTileW] = useState('600')
  const [pattern, setPattern] = useState<Pattern>('straight')
  const [tilePrice, setTilePrice] = useState('250')
  const [adhesivePrice, setAdhesivePrice] = useState('180')

  const r = useMemo(() => calcTiling({
    areaSqm: toolNum(area),
    tileLengthMm: toolNum(tileL),
    tileWidthMm: toolNum(tileW),
    groutJointMm: 3,
    layPattern: pattern,
    adhesiveKgPerM2: 4,
    adhesiveBagKg: 20,
    adhesivePricePerBag: toolNum(adhesivePrice),
    tilePricePerM2: toolNum(tilePrice),
    labourRatePerHour: 0,
  }), [area, tileL, tileW, pattern, tilePrice, adhesivePrice])

  return (
    <div className="grid gap-6 md:grid-cols-[1fr_380px] md:items-start">
      <div className="space-y-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Area &amp; tile</h2>
          <div className="mt-3 grid grid-cols-3 gap-3">
            <ToolField label="Area" value={area} onChange={setArea} unit="m²" />
            <ToolField label="Tile length" value={tileL} onChange={setTileL} unit="mm" step="10" />
            <ToolField label="Tile width" value={tileW} onChange={setTileW} unit="mm" step="10" />
          </div>
          <div className="mt-3">
            <ToolSelect
              label="Lay pattern"
              value={pattern}
              onChange={(v) => setPattern(v as Pattern)}
              options={[
                { value: 'straight', label: 'Straight (10% waste)' },
                { value: 'diagonal', label: 'Diagonal (15% waste)' },
                { value: 'herringbone', label: 'Herringbone (15% waste)' },
                { value: 'complex', label: 'Complex (20% waste)' },
              ]}
            />
          </div>
        </div>
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Prices</h2>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <ToolField label="Tile price" value={tilePrice} onChange={setTilePrice} unit="R/m²" />
            <ToolField label="Adhesive price" value={adhesivePrice} onChange={setAdhesivePrice} unit="R/bag" />
          </div>
        </div>
      </div>

      <ToolResultCard title="Your tiling estimate">
        <ToolResultRow label="Tiles to buy (incl. waste)" value={`${r.tilesNeeded} tiles`} />
        <ToolResultRow label="Adhesive bags" value={`${r.adhesiveBagsNeeded} bags`} />
        <ToolResultRow label="Estimated material cost" value={toolCurrency(r.tileCost + r.adhesiveCost)} strong />
      </ToolResultCard>
    </div>
  )
}
